import { useEffect, useState } from 'react';
import { tokens } from '../../theme';
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import { creatorApi, type ClipperConnection, apiBase } from '../../lib/api';

const API_BASE = apiBase();

const PROVIDERS: Array<{ key: 'tiktok' | 'youtube'; label: string; verb: string }> = [
  { key: 'tiktok', label: 'TikTok', verb: 'Connect TikTok' },
  { key: 'youtube', label: 'YouTube', verb: 'Connect YouTube' },
];

interface Props {
  connections: ClipperConnection[] | null;
  busyProvider: string | null;
  onDisconnect: (provider: string) => void;
}

export function ConnectionsTab({ connections, busyProvider, onDisconnect }: Props) {
  const byProvider = new Map<string, ClipperConnection>();
  for (const c of connections ?? []) byProvider.set(c.provider, c);

  const [payout, setPayout] = useState<{ connected: boolean; payouts_enabled: boolean } | null>(null);
  const [payoutBusy, setPayoutBusy] = useState(false);
  const [payoutErr, setPayoutErr] = useState<string | null>(null);

  useEffect(() => {
    creatorApi.connectStatus().then(setPayout).catch(() => setPayout({ connected: false, payouts_enabled: false }));
  }, []);

  async function connectPayouts() {
    setPayoutBusy(true);
    setPayoutErr(null);
    try {
      const { onboarding_url } = await creatorApi.connectStart();
      window.location.href = onboarding_url;
    } catch (e) {
      setPayoutErr(e instanceof Error ? e.message : 'Could not start payout connection. Please try again.');
      setPayoutBusy(false);
    }
  }

  const payoutsReady = payout?.payouts_enabled === true;

  return (
    <>
      <div
        style={{
          padding: 20, marginBottom: 16,
          background: tokens.color.paper['00'],
          border: `1px solid ${tokens.color.line[100]}`,
          borderRadius: 12,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ fontFamily: tokens.font.display, fontSize: 22, fontWeight: 400 }}>Payouts</div>
          <div style={{ marginTop: 6, fontSize: 13, color: tokens.color.ink[500] }}>
            {payoutsReady
              ? 'Connected. Verified earnings will be paid out to your account.'
              : 'Connect a payout account to get paid for verified views. Minimum payout is $50.'}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
          {payoutsReady
            ? <Badge status="active">Payouts enabled</Badge>
            : <Button size="sm" variant="primary" loading={payoutBusy} onClick={connectPayouts}>Connect payouts</Button>}
          {payoutErr && <div style={{ fontSize: 12, color: '#D43A2F', maxWidth: 280, textAlign: 'right' }}>{payoutErr}</div>}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {PROVIDERS.map(({ key, label, verb }) => {
          const conn = byProvider.get(key);
          return (
            <div
              key={key}
              style={{
                padding: 20,
                background: tokens.color.paper['00'],
                border: `1px solid ${tokens.color.line[100]}`,
                borderRadius: 12,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 16,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <div style={{ fontFamily: tokens.font.display, fontSize: 22, fontWeight: 400 }}>
                  {label}
                </div>
                {conn ? (
                  <div
                    style={{
                      marginTop: 6,
                      fontFamily: tokens.font.mono,
                      fontSize: 12,
                      color: tokens.color.ink[700],
                    }}
                  >
                    {conn.external_id ?? '(connected)'}
                    {conn.updated_at && (
                      <span style={{ color: tokens.color.ink[500], marginLeft: 12 }}>
                        updated {conn.updated_at.slice(0, 10)}
                      </span>
                    )}
                  </div>
                ) : (
                  <div style={{ marginTop: 6, fontSize: 13, color: tokens.color.ink[500] }}>
                    Not connected.
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {conn && <Badge status="active">Connected</Badge>}
                <a href={`${API_BASE}/auth/oauth/${key}`} style={{ textDecoration: 'none' }}>
                  <Button size="sm" variant={conn ? 'secondary' : 'primary'}>
                    {conn ? 'Reconnect' : verb}
                  </Button>
                </a>
                {conn && (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={busyProvider === key}
                    onClick={() => onDisconnect(key)}
                  >
                    Disconnect
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p style={{ marginTop: 32, fontSize: 12, color: tokens.color.ink[500], lineHeight: 1.6 }}>
        Connecting an account lets us verify views on the clips you post for active campaigns.
        You can disconnect at any time and we'll drop the stored token.
      </p>
    </>
  );
}
